import { useTranslation } from 'react-i18next';
import { DataTable, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import type { DataTableColumn } from '@ui-kit';
import type { CommonCodeValueResponse, ApiError } from '../types/code';

interface CodeItemTableProps {
  groupCode: string | null;
  groupName?: string;
  /** 현재 선택된 그룹이 systemManaged 인지 여부 — Round-EXEC #397 가드 판별용 */
  systemManaged?: boolean;
  items: CommonCodeValueResponse[];
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  /** Round-EXEC #397: ROLE_EXEC + !ROLE_ADMIN 컨텍스트 */
  isExecSandbox?: boolean;
  onAddItem: () => void;
  onEditItem: (item: CommonCodeValueResponse) => void;
  onDeleteItem: (item: CommonCodeValueResponse) => void;
  onRetry: () => void;
}

function SkeletonRow() {
  return (
    <tr>
      {[50, 70, 60, 40, 30, 60].map((w, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 14,
              width: `${w}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

function formatDate(isoString: string | null | undefined): string {
  if (!isoString) return '-';
  return new Date(isoString).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function CodeItemTable({
  groupCode,
  groupName,
  systemManaged,
  items,
  isLoading,
  isError,
  error,
  isExecSandbox = false,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onRetry,
}: CodeItemTableProps) {
  const { t } = useTranslation();
  // #429: EXEC 샌드박스 + systemManaged 그룹일 때만 코드값 mutation 버튼 비활성.
  // 비-system 그룹이면 EXEC 도 자유롭게 관리. ADMIN 은 isExecSandbox=false → 영향 없음.
  const isItemMutationBlocked = !!(systemManaged && isExecSandbox);
  // 잠금 tooltip: EXEC 컨텍스트면 exec 안내, 아니면 일반 시스템 보호 안내
  const lockedTooltip = isExecSandbox
    ? t('commonCode.exec.locked.tooltip')
    : t('commonCode.error.systemReadonly');
  const btnPrimary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  const btnSmall: React.CSSProperties = {
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-sm)',
    padding: '2px var(--spacing-2)',
    fontSize: 'var(--font-size-xs)',
    cursor: 'pointer',
    color: 'var(--color-text-secondary)',
    fontFamily: 'var(--font-family-sans)',
  };

  const btnDanger: React.CSSProperties = {
    ...btnSmall,
    color: 'var(--color-danger)',
    borderColor: 'color-mix(in srgb, var(--color-danger) 30%, var(--color-surface-border))',
  };

  const columns: DataTableColumn<CommonCodeValueResponse>[] = [
    {
      key: 'codeValue',
      header: t('commonCode.itemTable.column.codeValue'),
      render: (row) => (
        <span
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-primary)',
          }}
        >
          {row.codeValue}
        </span>
      ),
    },
    {
      key: 'defaultLabel',
      header: t('commonCode.itemTable.column.defaultLabel'),
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
          {row.defaultLabel}
        </span>
      ),
    },
    {
      key: 'labelKey',
      header: t('commonCode.itemTable.column.labelKey'),
      render: (row) => (
        <span
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
          }}
        >
          {row.labelKey || '-'}
        </span>
      ),
    },
    {
      key: 'sortOrder',
      header: t('commonCode.itemTable.column.sortOrder'),
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          {row.sortOrder}
        </span>
      ),
    },
    {
      key: 'useYn',
      header: t('commonCode.itemTable.column.useYn'),
      render: (row) =>
        row.useYn ? (
          <StatusPill tone="success">{t('commonCode.status.active')}</StatusPill>
        ) : (
          <StatusPill tone="neutral">{t('commonCode.status.inactive')}</StatusPill>
        ),
    },
    {
      key: 'createdAt',
      header: t('commonCode.itemTable.column.createdAt'),
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          {formatDate(row.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '120px',
      render: (row) => (
        <div style={{ display: 'flex', gap: 'var(--spacing-1)', alignItems: 'center' }}>
          <button
            type="button"
            aria-label={t('commonCode.itemTable.editAriaLabel', { codeValue: row.codeValue })}
            onClick={(e) => {
              e.stopPropagation();
              if (!isItemMutationBlocked) onEditItem(row);
            }}
            disabled={isItemMutationBlocked}
            title={isItemMutationBlocked ? lockedTooltip : undefined}
            style={{
              ...btnSmall,
              cursor: isItemMutationBlocked ? 'not-allowed' : 'pointer',
              opacity: isItemMutationBlocked ? 0.4 : 1,
            }}
          >
            {t('commonCode.itemTable.editBtn')}
          </button>
          <button
            type="button"
            aria-label={t('commonCode.itemTable.deleteAriaLabel', { codeValue: row.codeValue })}
            onClick={(e) => {
              e.stopPropagation();
              if (!isItemMutationBlocked) onDeleteItem(row);
            }}
            disabled={isItemMutationBlocked}
            title={isItemMutationBlocked ? lockedTooltip : undefined}
            style={{
              ...btnDanger,
              cursor: isItemMutationBlocked ? 'not-allowed' : 'pointer',
              opacity: isItemMutationBlocked ? 0.4 : 1,
            }}
          >
            {t('commonCode.itemTable.deleteBtn')}
          </button>
        </div>
      ),
    },
  ];

  // 그룹 미선택
  if (!groupCode) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--spacing-8)',
        }}
        role="region"
        aria-label={t('commonCode.itemTable.areaAriaLabel')}
      >
        <EmptyState
          title={t('commonCode.itemTable.noGroupSelected')}
          description={t('commonCode.itemTable.noGroupSelectedHint')}
        />
      </div>
    );
  }

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      role="region"
      aria-label={t('commonCode.itemTable.ariaLabel', { groupName: groupName ?? groupCode })}
    >
      {/* 항목 테이블 헤더 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--spacing-3) var(--spacing-4)',
          borderBottom: '1px solid var(--color-surface-border)',
          background: 'var(--color-surface-muted)',
          flexWrap: 'wrap',
          gap: 'var(--spacing-2)',
        }}
      >
        <div>
          <h3
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
            }}
          >
            {groupName ?? groupCode}
          </h3>
          <span
            style={{
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
            }}
          >
            {groupCode}
          </span>
        </div>
        <button
          type="button"
          onClick={() => { if (!isItemMutationBlocked) onAddItem(); }}
          disabled={isItemMutationBlocked}
          title={isItemMutationBlocked ? lockedTooltip : undefined}
          style={{
            ...btnPrimary,
            cursor: isItemMutationBlocked ? 'not-allowed' : 'pointer',
            opacity: isItemMutationBlocked ? 0.5 : 1,
          }}
          aria-label={t('commonCode.itemTable.addItemAriaLabel')}
        >
          {t('commonCode.itemTable.addItem')}
        </button>
      </div>

      {/* 항목 테이블 본문 */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          border: '1px solid var(--color-surface-border)',
          borderRadius: '0 0 var(--radius-md) var(--radius-md)',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* loading skeleton */}
        {isLoading && (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label={t('commonCode.itemTable.tableLoadingAriaLabel')}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {[
                t('commonCode.itemTable.column.codeValue'),
                t('commonCode.itemTable.column.defaultLabel'),
                t('commonCode.itemTable.column.labelKey'),
                t('commonCode.itemTable.column.sortOrder'),
                t('commonCode.itemTable.column.useYn'),
                t('commonCode.itemTable.column.createdAt'),
                '',
              ].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </tbody>
          </table>
        )}

        {/* error */}
        {isError && !isLoading && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <ErrorState
              title={t('commonCode.itemTable.errorTitle')}
              message={
                (error as ApiError)?.code === 'AUTH-403-001'
                  ? t('commonCode.itemTable.errorMessage.forbidden')
                  : ((error as ApiError)?.message ?? t('commonCode.itemTable.errorMessage.default'))
              }
              onRetry={onRetry}
            />
          </div>
        )}

        {/* empty */}
        {!isLoading && !isError && items.length === 0 && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('commonCode.itemTable.empty')}
              description={t('commonCode.itemTable.emptyHint')}
              action={
                <button type="button" onClick={onAddItem} style={btnPrimary}>
                  {t('commonCode.itemTable.addItem')}
                </button>
              }
            />
          </div>
        )}

        {/* DataTable */}
        {!isLoading && !isError && items.length > 0 && (
          <div role="region" aria-label={t('commonCode.itemTable.tableAriaLabel')} style={{ overflowX: 'auto' }}>
            <DataTable
              columns={columns}
              rows={items}
              rowKey={(row) => row.codeValue}
            />
          </div>
        )}
      </div>
    </div>
  );
}
